import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Tooltip from '@mui/joy/Tooltip';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Stack from '@mui/joy/Stack';
import Avatar from '@mui/joy/Avatar';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyIcon from '@mui/icons-material/Key';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

import { themeColors, shadows } from '../styles/theme';
import { useAuth } from '../contexts/AuthContext';
import apiKeyService from '../api/services/apiKeyService';
import { projectService } from '../api/services/projectService';
import { workspaceService } from '../api/services/workspaceService';


// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function ScopeChip({ scopeType }) {
  const map = {
    project:   { label: 'Project',   color: 'primary' },
    workspace: { label: 'Workspace', color: 'warning' },
    account:   { label: 'Account',   color: 'neutral' },
  };
  const { label, color } = map[scopeType] || { label: scopeType, color: 'neutral' };
  return <Chip size="sm" color={color} variant="soft">{label}</Chip>;
}

const inputSx = {
  border: 'none',
  boxShadow: shadows.sm,
  borderRadius: 2,
  backgroundColor: 'white',
  '--Input-focusedThickness': '0px',
  '&:focus-within': { boxShadow: shadows.focus },
  transition: 'all 0.2s ease',
};

const selectSx = {
  border: 'none',
  boxShadow: shadows.sm,
  borderRadius: 2,
  backgroundColor: 'white',
  '--Select-focusedThickness': '0px',
  '&:focus-within': { boxShadow: shadows.focus },
  transition: 'all 0.2s ease',
};

const primaryButtonSx = {
  background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
  boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
  fontWeight: 600,
  border: 'none',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
    background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
  },
  transition: 'all 0.2s ease',
};


// ── Create Key Modal ──────────────────────────────────────────────────────────

function CreateKeyModal({ open, onClose, onCreated, projects, workspaces }) {
  const [name, setName] = useState('');
  const [scopeType, setScopeType] = useState('project');
  const [scopeId, setScopeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setName('');
    setScopeType('project');
    setScopeId('');
    setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (scopeType !== 'account' && !scopeId) {
      setError('Please select a resource for this scope.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { name: name.trim() };
      if (scopeType === 'project')   payload.project   = Number(scopeId);
      if (scopeType === 'workspace') payload.workspace = Number(scopeId);
      const result = await apiKeyService.create(payload);
      reset();
      onCreated(result);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scopeResources = scopeType === 'project' ? projects : workspaces;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: 'min(520px, 92vw)',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 3,
          p: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            background: `linear-gradient(135deg, ${themeColors.primary[500]}08, ${themeColors.primary[500]}03)`,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            position: 'relative',
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              backgroundColor: 'white',
              boxShadow: shadows.sm,
              '&:hover': { backgroundColor: themeColors.neutral[100] },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 16px ${themeColors.primary[500]}30`,
                flexShrink: 0,
              }}
            >
              <VpnKeyIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900], mb: 0.5 }}>
                Create API Key
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                The key will be shown once — save it securely.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 4 }}>
          <Stack spacing={2.5}>
            {error && (
              <Alert
                color="danger"
                size="sm"
                startDecorator={<WarningAmberIcon />}
                sx={{ borderRadius: 2, border: 'none', boxShadow: shadows.sm }}
              >
                {error}
              </Alert>
            )}

            <FormControl>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Name
              </FormLabel>
              <Input
                placeholder="e.g. Client X Integration"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                sx={inputSx}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Scope
              </FormLabel>
              <Select
                value={scopeType}
                onChange={(_, val) => { setScopeType(val); setScopeId(''); }}
                disabled={loading}
                sx={selectSx}
              >
                <Option value="project">Project</Option>
                <Option value="workspace">Workspace</Option>
                <Option value="account">Account (all projects)</Option>
              </Select>
            </FormControl>

            {scopeType !== 'account' && (
              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                  {scopeType === 'project' ? 'Project' : 'Workspace'}
                </FormLabel>
                <Select
                  placeholder={`Select a ${scopeType}…`}
                  value={scopeId || ''}
                  onChange={(_, val) => setScopeId(val)}
                  disabled={loading}
                  sx={selectSx}
                >
                  {scopeResources.map((r) => (
                    <Option key={r.id} value={String(r.id)}>{r.name}</Option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${themeColors.neutral[100]}`,
            }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={handleClose}
              disabled={loading}
              sx={{
                color: themeColors.neutral[700],
                '&:hover': { backgroundColor: themeColors.neutral[100] },
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={loading} sx={primaryButtonSx}>
              Create Key
            </Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}


// ── Key Reveal Modal ──────────────────────────────────────────────────────────

function KeyRevealModal({ open, onClose, apiKey }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey?.key || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: 'min(560px, 92vw)',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 3,
          p: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            background: 'linear-gradient(135deg, #16a34a08, #16a34a03)',
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            position: 'relative',
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              backgroundColor: 'white',
              boxShadow: shadows.sm,
              '&:hover': { backgroundColor: themeColors.neutral[100] },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px #16a34a30',
                flexShrink: 0,
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900], mb: 0.5 }}>
                Key Created
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                Copy your key now — it will <strong>not</strong> be shown again.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 4 }}>
          <Stack spacing={2.5}>
            {/* Key display */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: themeColors.neutral[50],
                border: `1px solid ${themeColors.neutral[200]}`,
                borderRadius: 2,
                px: 2.5,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: 'Menlo, Monaco, monospace',
                  fontSize: '0.8125rem',
                  color: themeColors.neutral[800],
                  wordBreak: 'break-all',
                  lineHeight: 1.6,
                }}
              >
                {apiKey?.key}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton
                  size="sm"
                  variant={copied ? 'soft' : 'plain'}
                  color={copied ? 'success' : 'neutral'}
                  onClick={handleCopy}
                  sx={{ flexShrink: 0, borderRadius: 1.5 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Alert
              color="warning"
              size="sm"
              startDecorator={<WarningAmberIcon />}
              sx={{ borderRadius: 2, border: 'none', boxShadow: shadows.sm }}
            >
              Store this key in a secure location (e.g. environment variables). You cannot retrieve it later.
            </Alert>
          </Stack>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${themeColors.neutral[100]}`,
            }}
          >
            <Button onClick={onClose} sx={primaryButtonSx}>
              Done
            </Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}


// ── API Keys Section ──────────────────────────────────────────────────────────

function APIKeysSection() {
  const [keys, setKeys] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [fetchedKeys, fetchedProjects, fetchedWorkspaces] = await Promise.all([
        apiKeyService.list(),
        projectService.getAllProjects(),
        workspaceService.getAllWorkspaces(),
      ]);
      setKeys(fetchedKeys);
      setProjects(fetchedProjects);
      setWorkspaces(fetchedWorkspaces);
    } catch {
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreated = (newKey) => {
    setCreateOpen(false);
    setRevealedKey(newKey);
    setKeys((prev) => [newKey, ...prev]);
  };

  const handleRevoke = async (id) => {
    setRevoking(id);
    try {
      await apiKeyService.revoke(id);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch {
      // silently restore; could show an error toast here
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Box>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
            API Keys
          </Typography>
          <Typography level="body-sm" sx={{ color: themeColors.neutral[500], mt: 0.5 }}>
            Use these keys to access your project data programmatically.
          </Typography>
        </Box>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ ...primaryButtonSx, flexShrink: 0 }}
        >
          Create Key
        </Button>
      </Box>

      {/* States */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loading && error && (
        <Alert color="danger" size="sm" sx={{ borderRadius: 2, border: 'none', boxShadow: shadows.sm }}>
          {error}
        </Alert>
      )}

      {!loading && !error && keys.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            color: themeColors.neutral[400],
            border: `1.5px dashed ${themeColors.neutral[200]}`,
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              backgroundColor: themeColors.neutral[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <KeyIcon sx={{ fontSize: 28, color: themeColors.neutral[400] }} />
          </Box>
          <Typography level="body-sm" sx={{ fontWeight: 600, color: themeColors.neutral[600] }}>
            No API keys yet
          </Typography>
          <Typography level="body-xs" sx={{ color: themeColors.neutral[400], mt: 0.5 }}>
            Create one to get started.
          </Typography>
        </Box>
      )}

      {!loading && !error && keys.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${themeColors.neutral[200]}`,
            borderRadius: 2.5,
            overflow: 'hidden',
          }}
        >
          {/* Table header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 140px 120px 100px 48px',
              gap: 1,
              px: 2.5,
              py: 1.25,
              backgroundColor: themeColors.neutral[50],
              borderBottom: `1px solid ${themeColors.neutral[200]}`,
            }}
          >
            {['Name', 'Scope', 'Resource', 'Created', 'Last Used', ''].map((h) => (
              <Typography
                key={h}
                level="body-xs"
                sx={{
                  fontWeight: 700,
                  color: themeColors.neutral[500],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {keys.map((key, idx) => (
            <Box
              key={key.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 140px 120px 100px 48px',
                gap: 1,
                alignItems: 'center',
                px: 2.5,
                py: 2,
                borderBottom: idx < keys.length - 1 ? `1px solid ${themeColors.neutral[100]}` : 'none',
                '&:hover': { backgroundColor: themeColors.neutral[50] },
                transition: 'background 0.12s ease',
              }}
            >
              {/* Name + prefix */}
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 600, color: themeColors.neutral[900] }}>
                  {key.name}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{ fontFamily: 'monospace', color: themeColors.neutral[400], mt: 0.25 }}
                >
                  {key.prefix}…
                </Typography>
              </Box>

              <ScopeChip scopeType={key.scope_type} />

              <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                {key.scope_label}
              </Typography>

              <Typography level="body-sm" sx={{ color: themeColors.neutral[500] }}>
                {formatDate(key.created_at)}
              </Typography>

              <Typography level="body-sm" sx={{ color: themeColors.neutral[500] }}>
                {formatDate(key.last_used_at)}
              </Typography>

              <Tooltip title="Revoke key">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => handleRevoke(key.id)}
                  loading={revoking === key.id}
                  sx={{ borderRadius: 1.5 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <CreateKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        projects={projects}
        workspaces={workspaces}
      />
      <KeyRevealModal
        open={!!revealedKey}
        onClose={() => setRevealedKey(null)}
        apiKey={revealedKey}
      />
    </Box>
  );
}


// ── API Docs ──────────────────────────────────────────────────────────────────

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box sx={{ position: 'relative', backgroundColor: '#1a1d2e', borderRadius: 2, p: 2.5, pr: 6 }}>
      <Typography
        sx={{
          fontFamily: 'Menlo, Monaco, Consolas, monospace',
          fontSize: '0.8125rem',
          color: '#e2e8f0',
          whiteSpace: 'pre',
          overflowX: 'auto',
          lineHeight: 1.8,
          display: 'block',
        }}
      >
        {code}
      </Typography>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <IconButton
          size="sm"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: copied ? '#16a34a22' : 'rgba(255,255,255,0.08)',
            color: copied ? '#4ade80' : '#94a3b8',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
            borderRadius: 1.5,
            minWidth: 0,
          }}
        >
          {copied ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function MethodBadge({ method }) {
  const styles = {
    GET:    { backgroundColor: '#dcfce7', color: '#16a34a' },
    POST:   { backgroundColor: '#dbeafe', color: '#2563eb' },
    DELETE: { backgroundColor: '#fee2e2', color: '#dc2626' },
  };
  const s = styles[method] || { backgroundColor: '#f1f5f9', color: '#64748b' };
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.35,
        borderRadius: 1,
        fontFamily: 'Menlo, Monaco, Consolas, monospace',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        flexShrink: 0,
        userSelect: 'none',
        ...s,
      }}
    >
      {method}
    </Box>
  );
}

function ParamRow({ name, type, required, description }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '160px 80px 76px 1fr',
        gap: 2,
        py: 1.5,
        alignItems: 'flex-start',
        '& + &': { borderTop: `1px solid ${themeColors.neutral[100]}` },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Menlo, Monaco, Consolas, monospace',
          fontSize: '0.8125rem',
          color: themeColors.neutral[800],
          fontWeight: 600,
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Menlo, Monaco, Consolas, monospace',
          fontSize: '0.75rem',
          color: themeColors.neutral[500],
        }}
      >
        {type}
      </Typography>
      {required ? (
        <Chip size="sm" color="danger" variant="soft" sx={{ fontSize: '0.7rem', height: 20 }}>
          required
        </Chip>
      ) : (
        <Typography level="body-xs" sx={{ color: themeColors.neutral[400], pt: 0.25 }}>
          optional
        </Typography>
      )}
      <Typography level="body-sm" sx={{ color: themeColors.neutral[600], lineHeight: 1.6 }}>
        {description}
      </Typography>
    </Box>
  );
}

function EndpointCard({ method, path, description, queryParams, bodyParams, responseExample }) {
  return (
    <Box
      sx={{
        border: `1px solid ${themeColors.neutral[200]}`,
        borderRadius: 2.5,
        overflow: 'hidden',
        mb: 2.5,
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2.5,
          backgroundColor: themeColors.neutral[50],
          borderBottom: `1px solid ${themeColors.neutral[200]}`,
          flexWrap: 'wrap',
        }}
      >
        <MethodBadge method={method} />
        <Typography
          sx={{
            fontFamily: 'Menlo, Monaco, Consolas, monospace',
            fontSize: '0.875rem',
            color: themeColors.neutral[800],
            flex: 1,
            minWidth: 160,
          }}
        >
          {path}
        </Typography>
        <Typography level="body-sm" sx={{ color: themeColors.neutral[500] }}>
          {description}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3 }}>
        {queryParams && (
          <Box sx={{ mb: 3 }}>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 1.5, color: themeColors.neutral[700] }}>
              Query Parameters
            </Typography>
            <Box
              sx={{
                border: `1px solid ${themeColors.neutral[200]}`,
                borderRadius: 2,
                px: 2.5,
                overflow: 'hidden',
              }}
            >
              {queryParams.map((p) => <ParamRow key={p.name} {...p} />)}
            </Box>
          </Box>
        )}

        {bodyParams && (
          <Box sx={{ mb: 3 }}>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 1.5, color: themeColors.neutral[700] }}>
              Request Body
              <Typography
                component="span"
                level="body-xs"
                sx={{ ml: 1.5, color: themeColors.neutral[400], fontWeight: 400, fontFamily: 'monospace' }}
              >
                application/json
              </Typography>
            </Typography>
            <Box
              sx={{
                border: `1px solid ${themeColors.neutral[200]}`,
                borderRadius: 2,
                px: 2.5,
                overflow: 'hidden',
              }}
            >
              {bodyParams.map((p) => <ParamRow key={p.name} {...p} />)}
            </Box>
          </Box>
        )}

        <Box>
          <Typography level="title-sm" sx={{ fontWeight: 700, mb: 1.5, color: themeColors.neutral[700] }}>
            Response
            <Typography
              component="span"
              level="body-xs"
              sx={{ ml: 1.5, color: '#16a34a', fontWeight: 600, fontFamily: 'monospace' }}
            >
              200 OK
            </Typography>
          </Typography>
          <CodeBlock code={responseExample} />
        </Box>
      </Box>
    </Box>
  );
}

function DocsSection({ children }) {
  return (
    <Typography
      level="body-xs"
      sx={{
        fontWeight: 700,
        color: themeColors.neutral[400],
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        mb: 1.5,
        mt: 4,
        display: 'block',
      }}
    >
      {children}
    </Typography>
  );
}

function APIDocsSection() {
  const baseUrl = `${window.location.origin}/api`;

  return (
    <Box>
      {/* Section header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
          API Reference
        </Typography>
        <Typography level="body-sm" sx={{ color: themeColors.neutral[500], mt: 0.5 }}>
          Programmatic access to your projects and jobs.
        </Typography>
      </Box>

      {/* Authentication */}
      <Box sx={{ mb: 4 }}>
        <Typography level="title-lg" sx={{ fontWeight: 700, color: themeColors.neutral[800], mb: 0.75 }}>
          Authentication
        </Typography>
        <Typography level="body-sm" sx={{ color: themeColors.neutral[600], mb: 2 }}>
          Include your API key in every request using the{' '}
          <Typography
            component="span"
            sx={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: '0.8125rem', color: themeColors.neutral[700] }}
          >
            Authorization
          </Typography>{' '}
          header. Keys are scoped to a project, workspace, or your entire account.
        </Typography>
        <CodeBlock code={`Authorization: Bearer voc_your_api_key_here`} />
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            backgroundColor: themeColors.primary[50],
            borderRadius: 2,
            border: `1px solid ${themeColors.primary[100]}`,
          }}
        >
          <VpnKeyIcon sx={{ fontSize: 16, color: themeColors.primary[500], flexShrink: 0 }} />
          <Typography level="body-sm" sx={{ color: themeColors.primary[700] }}>
            Manage your API keys in the <strong>API Keys</strong> tab.
          </Typography>
        </Box>
      </Box>

      {/* Base URL */}
      <Box
        sx={{
          mb: 5,
          pb: 5,
          borderBottom: `1px solid ${themeColors.neutral[100]}`,
        }}
      >
        <Typography level="title-lg" sx={{ fontWeight: 700, color: themeColors.neutral[800], mb: 0.75 }}>
          Base URL
        </Typography>
        <Typography level="body-sm" sx={{ color: themeColors.neutral[600], mb: 2 }}>
          All endpoints below are prefixed with:
        </Typography>
        <CodeBlock code={`${baseUrl}/v1/`} />
      </Box>

      {/* Endpoints */}
      <Box>
        <Typography level="title-lg" sx={{ fontWeight: 700, color: themeColors.neutral[800], mb: 0.5 }}>
          Endpoints
        </Typography>

        <DocsSection>Projects</DocsSection>

        <EndpointCard
          method="GET"
          path="/api/v1/projects/"
          description="List all accessible projects"
          responseExample={JSON.stringify([
            {
              id: 1,
              name: "HACCP Monitoring",
              url_name: "haccp",
              description: "Freezer monitoring system",
              mode: "stateful_job",
              created_at: "2025-01-01T00:00:00Z",
            },
          ], null, 2)}
        />

        <EndpointCard
          method="GET"
          path="/api/v1/projects/{id}/"
          description="Get a single project"
          responseExample={JSON.stringify(
            {
              id: 1,
              name: "HACCP Monitoring",
              url_name: "haccp",
              description: "Freezer monitoring system",
              mode: "stateful_job",
              created_at: "2025-01-01T00:00:00Z",
            },
            null, 2
          )}
        />

        <DocsSection>Jobs</DocsSection>

        <EndpointCard
          method="GET"
          path="/api/v1/jobs/"
          description="List all jobs"
          queryParams={[
            {
              name: "project_id",
              type: "integer",
              required: false,
              description: "Filter jobs by project.",
            },
          ]}
          responseExample={JSON.stringify([
            {
              id: 101,
              name: "Shift 1 \u2014 Jan 2025",
              project: 1,
              outputs: { docx: "https://..." },
              base_output: { "...": "structured job data" },
              is_locked: false,
              created_at: "2025-01-01T08:00:00Z",
              updated_at: "2025-01-01T16:00:00Z",
            },
          ], null, 2)}
        />

        <EndpointCard
          method="GET"
          path="/api/v1/jobs/{id}/"
          description="Get a job with transcripts and photos"
          responseExample={JSON.stringify(
            {
              id: 101,
              name: "Shift 1 \u2014 Jan 2025",
              project: 1,
              outputs: { docx: "https://..." },
              base_output: { "...": "structured job data" },
              is_locked: false,
              created_at: "2025-01-01T08:00:00Z",
              updated_at: "2025-01-01T16:00:00Z",
              transcripts: [
                { id: 1, content: "...", length: 120, guest_author_name: null, created_at: "..." },
              ],
              photos: [
                { id: 1, image_url: "https://...", order: 0, created_at: "..." },
              ],
            },
            null, 2
          )}
        />

        <DocsSection>Search</DocsSection>

        <EndpointCard
          method="POST"
          path="/api/v1/search/"
          description="Semantic search across job outputs"
          bodyParams={[
            {
              name: "query",
              type: "string",
              required: true,
              description: "Natural language search query.",
            },
            {
              name: "project_id",
              type: "integer",
              required: false,
              description: "Limit search to a specific project.",
            },
            {
              name: "path",
              type: "string",
              required: false,
              description: 'Filter by field path (e.g. "comments", "items.issue").',
            },
            {
              name: "limit",
              type: "integer",
              required: false,
              description: "Max results to return (default 20, max 100).",
            },
          ]}
          responseExample={JSON.stringify([
            {
              similarity: 0.92,
              path: "items.comments",
              item_index: 3,
              text: "Ice visible on container lid",
              item_data: {
                container_id: "C-14",
                temp: -18.2,
                comments: "Ice visible on container lid",
              },
              job: {
                id: 101,
                name: "Shift 1 \u2014 Jan 2025",
                project: 1,
                created_at: "...",
                updated_at: "...",
              },
            },
          ], null, 2)}
        />
      </Box>
    </Box>
  );
}


// ── Settings Page ─────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'api-keys', label: 'API Keys', icon: <KeyIcon sx={{ fontSize: 17 }} /> },
  { id: 'api-docs', label: 'API Reference', icon: <IntegrationInstructionsIcon sx={{ fontSize: 17 }} /> },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('api-keys');

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: themeColors.neutral[50] }}>
      {/* Header */}
      <Box
        sx={{
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          backgroundColor: 'white',
          boxShadow: shadows.sm,
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}
      >
        {/* Left: back + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            size="sm"
            variant="soft"
            color="neutral"
            onClick={() => navigate('/projects')}
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
            Settings
          </Typography>
        </Box>

        {/* Right: user menu */}
        <Dropdown>
          <MenuButton
            variant="plain"
            sx={{
              borderRadius: 2.5,
              minHeight: 44,
              px: 1.5,
              gap: 1.5,
              border: 'none',
              '&:hover': { backgroundColor: themeColors.neutral[100], boxShadow: shadows.sm },
              transition: 'all 0.2s ease',
            }}
            endDecorator={<KeyboardArrowDownIcon />}
          >
            <Avatar
              size="sm"
              sx={{
                backgroundColor: themeColors.primary[100],
                color: themeColors.primary[600],
                '--Avatar-size': '36px',
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
              <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                {user?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>
                Creator
              </Typography>
            </Box>
          </MenuButton>

          <Menu
            placement="bottom-end"
            sx={{
              minWidth: 220,
              zIndex: 1400,
              '--List-padding': '8px',
              boxShadow: shadows.xl,
              border: 'none',
              borderRadius: 2.5,
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography level="title-sm" sx={{ fontWeight: 600 }}>{user?.email || 'User'}</Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>Creator Account</Typography>
            </Box>

            <Box sx={{ mx: 2, my: 1, height: '1px', backgroundColor: themeColors.neutral[100] }} />

            <MenuItem
              onClick={() => navigate('/settings')}
              sx={{
                py: 1.5, mx: 1, borderRadius: 1.5,
                '&:hover': { backgroundColor: themeColors.neutral[100] },
                transition: 'all 0.15s ease',
              }}
            >
              <SettingsIcon fontSize="small" />
              <Typography level="body-sm" sx={{ ml: 1.5 }}>Settings</Typography>
            </MenuItem>

            <Box sx={{ mx: 2, my: 1, height: '1px', backgroundColor: themeColors.neutral[100] }} />

            <MenuItem
              onClick={logout}
              sx={{
                py: 1.5, mx: 1, borderRadius: 1.5,
                color: themeColors.danger,
                '&:hover': { backgroundColor: `${themeColors.danger}10` },
                transition: 'all 0.15s ease',
              }}
            >
              <LogoutIcon fontSize="small" />
              <Typography level="body-sm" sx={{ ml: 1.5, fontWeight: 600 }}>Sign Out</Typography>
            </MenuItem>
          </Menu>
        </Dropdown>
      </Box>

      {/* Layout */}
      <Box sx={{ display: 'flex', maxWidth: 1100, mx: 'auto', px: 3, pt: 4, gap: 4 }}>
        {/* Left nav */}
        <Box sx={{ width: 200, flexShrink: 0 }}>
          {NAV_ITEMS.map((item) => (
            <Box
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: activeTab === item.id ? themeColors.primary[50] : 'transparent',
                color: activeTab === item.id ? themeColors.primary[700] : themeColors.neutral[600],
                fontWeight: activeTab === item.id ? 600 : 400,
                fontSize: '0.875rem',
                transition: 'all 0.12s ease',
                '&:hover': {
                  backgroundColor: activeTab === item.id
                    ? themeColors.primary[50]
                    : themeColors.neutral[100],
                },
              }}
            >
              {item.icon}
              {item.label}
            </Box>
          ))}
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: 'white',
            borderRadius: 3,
            border: `1px solid ${themeColors.neutral[200]}`,
            boxShadow: shadows.sm,
            p: 4,
            mb: 4,
          }}
        >
          {activeTab === 'api-keys' && <APIKeysSection />}
          {activeTab === 'api-docs' && <APIDocsSection />}
        </Box>
      </Box>
    </Box>
  );
}
